import { useEffect, useRef, useState } from 'react';

import type { Content } from '../global-types/Content';
import { useHandleCallback } from './useHandleCallback';
import { useHandleCallbackUrl } from './useHandleCallbackUrl';
import useIsInViewport from './useIsInViewport';

type EventType = 'visible';

function useTracking(content: Content, viewId: number | undefined) {
  const [hasFiredVisibleEvent, setHasFiredVisibleEvent] = useState<boolean>(false);
  const trackVisibilityElement = useRef(null);
  const isInViewport = useIsInViewport(trackVisibilityElement, viewId !== undefined);
  const { mutate: sendCallback } = useHandleCallback();
  const { mutate: sendCallbackUrl } = useHandleCallbackUrl();

  const activatedVisible = import.meta.env.VITE_USE_TRACKING_VISIBLE === 'true';
  const forceTracking = content.forceTracking ?? false;

  const disableVisibleTracking = !activatedVisible && !forceTracking;

  useEffect(() => {
    if (disableVisibleTracking) {
      return;
    }
    setHasFiredVisibleEvent(false);
  }, [viewId, disableVisibleTracking]);

  useEffect(() => {
    if (disableVisibleTracking) {
      return;
    }
    if (isInViewport && !hasFiredVisibleEvent && viewId) {
      const eventType: EventType = 'visible';

      const contentView = content.contentViews?.find((cv) => cv.contentViewId === viewId);

      if (contentView?.tracking) {
        const { baseUrl, callbackPath, payload } = contentView.tracking;
        if (payload) {
          sendCallback({
            url: `${baseUrl ?? ''}${callbackPath}`,
            payload: { ...payload, eventType },
          });
        } else {
          sendCallbackUrl({
            baseUrl,
            callbackPath: `${contentView?.tracking.callbackPath}&eventType=${eventType}`,
          });
        }
      }
      setHasFiredVisibleEvent(true);
    }
  }, [isInViewport, hasFiredVisibleEvent, content, viewId, sendCallback, sendCallbackUrl, disableVisibleTracking]);

  return trackVisibilityElement;
}

export default useTracking;
